import DeleteIcon from '@mui/icons-material/Delete'
import ReplayIcon from '@mui/icons-material/Replay'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

interface MonitoringSiteItemProps {
  monitoringSite: {
    properties: {
      id?: string | number
      name: string
      isRandomSite?: boolean
      newManualSite?: boolean
      randomizeCounter?: number
      hasAssociatedTasks?: boolean
    }
    geometry: {
      coordinates: [number, number]
    }
  }
  wasSaved: boolean
  isRandomSite: boolean
  reRandomize?: boolean
  onNameChange?: (newName: string) => void
  onReRandomize?: () => void
  onDelete?: () => void
}

export const MonitoringSiteItem = ({
  monitoringSite,
  wasSaved,
  isRandomSite,
  reRandomize,
  onNameChange,
  onReRandomize,
  onDelete,
}: MonitoringSiteItemProps) => {
  const hasAssociatedTasks = monitoringSite.properties.hasAssociatedTasks === true
  // Solo proteger sitios guardados con tasks asociadas
  const isDeletable = !wasSaved || (wasSaved && !hasAssociatedTasks)

  // El nombre solo es editable inline para sitios aleatorios aún sin guardar.
  // Los manuales se nombran exclusivamente desde el modal, por eso van como label.
  const isNameEditable = isRandomSite && !wasSaved
  const tooltipMessage =
    wasSaved && isRandomSite
      ? 'Los sitios aleatorios no se pueden editar'
      : wasSaved && hasAssociatedTasks
        ? 'Este sitio tiene tareas asociadas y no puede editarse'
        : ''

  return (
    <Stack
      alignItems="center"
      direction="row"
      spacing={isRandomSite ? 2 : 1}
      sx={{ p: 1.5, justifyContent: 'space-between' }}
    >
      {/* Nombre */}
      <Box sx={{ flexGrow: 1 }}>
        {isNameEditable ? (
          <Tooltip title={tooltipMessage}>
            <TextField
              defaultValue={monitoringSite.properties.name}
              placeholder="Nombre del sitio"
              size="small"
              variant="standard"
              onChange={e => {
                if (e.target.value.length && onNameChange) {
                  onNameChange(e.target.value)
                }
              }}
            />
          </Tooltip>
        ) : (
          <Tooltip title={tooltipMessage}>
            <Typography variant="body1">{monitoringSite.properties.name}</Typography>
          </Tooltip>
        )}
      </Box>

      {/* Coordenadas */}
      <Typography variant="body1">
        {`${monitoringSite.geometry.coordinates?.[1]?.toFixed(11) ?? 0}, ${
          monitoringSite.geometry.coordinates?.[0]?.toFixed(11) ?? 0
        }`}
      </Typography>

      {/* Acciones */}
      {isRandomSite && reRandomize && !wasSaved && (
        <Stack alignItems="center" direction="row" spacing={1}>
          <Tooltip title="Aleatorizar sitio - Máximo 2 iteraciones">
            <IconButton
              color="primary"
              disabled={(monitoringSite.properties.randomizeCounter || 0) > 1}
              size="small"
              onClick={onReRandomize}
            >
              <ReplayIcon />
            </IconButton>
          </Tooltip>
          <Typography sx={{ fontSize: 11 }} variant="body2">
            {monitoringSite.properties.randomizeCounter || 0}/2
          </Typography>
        </Stack>
      )}

      {!isRandomSite && (
        <Stack alignItems="center" direction="row" spacing={1}>
          {onDelete && (
            <Tooltip
              title={
                hasAssociatedTasks && wasSaved
                  ? 'Este sitio tiene tareas asociadas y no puede eliminarse'
                  : 'Eliminar sitio manual'
              }
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 40,
                  height: 40,
                }}
              >
                <IconButton color="error" disabled={!isDeletable} size="small" onClick={onDelete}>
                  <DeleteIcon />
                </IconButton>
              </Box>
            </Tooltip>
          )}
        </Stack>
      )}
    </Stack>
  )
}
